import { Module } from '@gongxigames/framework/slot/fragments/module/Module';
import { GlobalEventsMapForBackwardCompatibility } from '@gongxigames/framework/slot/events';
import { CoreDI, IBrowser, ICurrencyFormatter, inject, LanguageService } from '@gongxigames/framework/core';
import { LogicEvents } from '@gongxigames/framework/slot/services/logic';
import { ILogic, SystemPopup } from '@gongxigames/framework/slot/types';
import { Action } from 'com/gongxigames/math/core/game/enums/Action';
import { ReelsEvents } from '@gongxigames/framework/slot/modules/reels/ReelsEvents';
import { IPromotionLogic, ILogicPromotionFunctional, PromotionPopupFactory, CounterData } from '@gongxigames/framework/promotions';
import {strictUiEmitter} from '@gongxigames/framework/slotV2/globals/strictUiEmitter';

export class BasePromotionModule<EventsMap> extends Module<null, GlobalEventsMapForBackwardCompatibility & EventsMap> {
    public static readonly NAME: symbol = Symbol.for('PromotionModule');

    @inject(CoreDI.CurrencyFormatter)
    private currencyFormatter!: ICurrencyFormatter;

    @inject(CoreDI.LanguageService)
    private languageService!: LanguageService;

    @inject(CoreDI.Browser)
    protected _browser!: IBrowser;

    private promotion!: IPromotionLogic;

    public popups!: PromotionPopupFactory;

    private _currentlyShownPopups: SystemPopup[] = [];

    public init(): void {
        this.promotion = this.getLogic<ILogic & ILogicPromotionFunctional>().getPromotion();
        this.popups = new PromotionPopupFactory(this.languageService, this.currencyFormatter, this._browser);
        super.init();
    }

    public get nextActionIsSpin(): boolean {
        return this.logic.nextAction === Action.SPIN;
    }

    public getPromotion<PromotionLogic extends IPromotionLogic>(): PromotionLogic {
        return this.promotion as PromotionLogic;
    }

    public showPopup(popup: SystemPopup): void {
        this._currentlyShownPopups.push(popup);
        this.getEventManager<GlobalEventsMapForBackwardCompatibility>().emit('General.Popups.Add', popup);
    }

    public hidePopup(popup: SystemPopup): void {
        this._currentlyShownPopups = this._currentlyShownPopups.filter((p) => p !== popup);
        this.getEventManager<GlobalEventsMapForBackwardCompatibility>().emit('General.Popups.Remove', popup);
    }

    public hasPopupActive(popup?: SystemPopup): boolean {
        return popup ? this._currentlyShownPopups.includes(popup) : false;
    }

    public emitHideBetPanel(): void {
        if (this.getPromotion().getData().type === 'awarded-feature' && this.settings.get('hideBetPanelDuringAwardedFeature')) {
            strictUiEmitter.emit('UI.bet.hide');
        }
    }

    public emitShowBetPanel(): void {
        strictUiEmitter.emit('UI.bet.show');
    }

    protected setupEvents(): void {
        super.setupEvents();

        this.onCurrentStateHandle('General.Flow.WAITING_OF_RESTORE_GAME');
        this.onCurrentStateHandle(LogicEvents.ROUND_START);
        this.onCurrentStateHandle('General.Flow.PAID_ROUND_ENDED');
        this.onCurrentStateHandle('General.Game.STARTED');
        this.onCurrentStateHandle(ReelsEvents.REELS_SPINNING_STARTED, -1);
        this.onCurrentStateHandle(ReelsEvents.SPIN_ENDED);
        this.onCurrentStateHandle(ReelsEvents.ALL_REELS_STOPPED);
        this.onAsyncCurrentStateHandle(LogicEvents.RESPONSE);
        this.onCurrentStateHandle(LogicEvents.BALANCE_UPDATED);
    }

    public activateCounter(): void {
        this.getEventManager<GlobalEventsMapForBackwardCompatibility>().emit('General.Promotion.ActivateCounter', this.getCounterData());
    }

    public updateCounter(): void {
        this.getEventManager<GlobalEventsMapForBackwardCompatibility>().emit('General.Promotion.UpdateCounter', this.getCounterData());
    }

    protected getCounterData(): CounterData {
        const promotionData = this.getPromotion().getData();
        const settingsPrefix = promotionData.type === 'free-round' ? 'freeRounds' : 'awardedFeature';
        const valuedAtEnabled = this.settings.get(`${settingsPrefix}ValuedAtEnabled`);
        const { left, total, totalBet, totalPrice, type } = promotionData;

        return { left, total, bet: totalBet, price: totalPrice, type, valuedAtEnabled };
    }
}
